import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ClipboardList,
  Users,
  CheckCircle2,
  XCircle,
  Award,
  BarChart3,
  Mail,
  AlertCircle,
  Clock,
  X,
} from "lucide-react";
import AssessmentClient, {
  AssessmentResultRow,
  AssessmentResultsResponse,
} from "../data/api/AssessmentClient";
import Loader from "../../../shared/components/Loader";
import "../styles/AssessmentResultsStyles.css";
const gradeConfig = (score: number | string | null | undefined) => {
  if (score === null || score === undefined || score === "NA") {
    return { label: "Not submitted", bg: "#f1f5f9", color: "#64748b" };
  }
  const num = Number(score);
  if (num >= 80) return { label: "Excellent", bg: "#dcfce7", color: "#166534" };
  if (num >= 60) return { label: "Good", bg: "#dbeafe", color: "#1d4ed8" };
  if (num >= 40) return { label: "Average", bg: "#fef3c7", color: "#b45309" };
  return { label: "Poor", bg: "#fee2e2", color: "#b91c1c" };
};

const AssessmentResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const assessmentId = id || "";

  const [rows, setRows] = useState<AssessmentResultRow[]>([]);
  const [total, setTotal] = useState(0);
  const [assessmentName, setAssessmentName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCandidateRow, setSelectedCandidateRow] =
    useState<AssessmentResultRow | null>(null);
  const [detailedResult, setDetailedResult] = useState<any | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const handleRowClick = (row: AssessmentResultRow) => {
    navigate(
      `/dashboard/assessments/${assessmentId}/results/${row.candidate_id}`,
    );
  };

  useEffect(() => {
    const loadResults = async () => {
      if (!assessmentId) return;
      setLoading(true);
      try {
        const data: AssessmentResultsResponse =
          await AssessmentClient.getAssessmentResults(assessmentId);
        setRows(data.records || []);
        setTotal(data.total_no_of_records ?? (data.records || []).length);
        if (data.assessment_entity_name) {
          setAssessmentName(data.assessment_entity_name);
        }
      } catch (e: any) {
        setError("Failed to fetch assessment results. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    loadResults();
  }, [assessmentId]);

  const formatDateTime = (raw?: string) => {
    if (!raw) return "-";
    // If it already contains timezone info (+ or Z), don't append Z
    const iso =
      raw.endsWith("Z") ||
      raw.includes("+") ||
      (raw.includes("-") && raw.split("T")[1]?.includes("-"))
        ? raw
        : raw + "Z";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return raw;

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, "0");

    return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
  };

  const attendedCount = rows.filter((r) => r.attended).length;
  const notAttendedCount = rows.filter((r) => !r.attended).length;
  const submittedCount = rows.filter(
    (r) => r.score !== null && r.score !== undefined && r.score !== "NA",
  ).length;

  const getStatusBadge = (row: AssessmentResultRow) => {
    const status = (row.status || "").toUpperCase();
    if (status === "NOTSTARTED") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 600,
            backgroundColor: "#f1f5f9",
            color: "#475569",
          }}
        >
          <Clock size={13} /> Not Started
        </span>
      );
    }
    if (status === "IN_PROGRESS" || status === "INITIATED") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 600,
            backgroundColor: "#eff6ff",
            color: "#1d4ed8",
          }}
        >
          <Clock size={13} /> In Progress
        </span>
      );
    }
    if (status === "COMPLETED") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 600,
            backgroundColor: "#fef3c7",
            color: "#b45309",
          }}
        >
          <Clock size={13} /> Submitted
        </span>
      );
    }
    if (row.attended) {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 600,
            backgroundColor: "#dcfce7",
            color: "#166534",
          }}
        >
          <CheckCircle2 size={13} /> Scored
        </span>
      );
    }
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 600,
          backgroundColor: "#fee2e2",
          color: "#b91c1c",
        }}
      >
        <XCircle size={13} /> Not Attended
      </span>
    );
  };

  return (
    <motion.div
      className="learner-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader isLoading={loading} message="Loading assessment results..." />

      <div className="lp-hero" style={{ flexDirection: "column", gap: "24px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            width: "100%",
          }}
        >
          <button
            className="results-back-btn"
            onClick={() => navigate("/dashboard/assessments")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "#fff",
              border: "1px solid #e2e8f0",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <ChevronLeft size={20} color="#0f172a" />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <h1 style={{ margin: 0 }}>
              {assessmentName || "Assessment Results"}
            </h1>
            <span className="lp-hero-chip">
              Candidate attendance and scores
            </span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          <div className="results-stat-card">
            <div
              className="results-stat-icon"
              style={{ background: "#e0e7ff", color: "#3730a3" }}
            >
              <Users size={20} strokeWidth={2.5} />
            </div>
            <div className="results-stat-label">Candidates</div>
            <div className="results-stat-value">{total}</div>
          </div>
          <div className="results-stat-card">
            <div
              className="results-stat-icon"
              style={{ background: "#dcfce7", color: "#166534" }}
            >
              <CheckCircle2 size={20} strokeWidth={2.5} />
            </div>
            <div className="results-stat-label">Attended</div>
            <div className="results-stat-value">{attendedCount}</div>
          </div>
          <div className="results-stat-card">
            <div
              className="results-stat-icon"
              style={{ background: "#dbeafe", color: "#1d4ed8" }}
            >
              <BarChart3 size={20} strokeWidth={2.5} />
            </div>
            <div className="results-stat-label">Submitted</div>
            <div className="results-stat-value">{submittedCount}</div>
          </div>
          <div className="results-stat-card">
            <div
              className="results-stat-icon"
              style={{ background: "#fef3c7", color: "#b45309" }}
            >
              <Award size={20} strokeWidth={2.5} />
            </div>
            <div className="results-stat-label">Avg Score</div>
            <div className="results-stat-value">
              {submittedCount > 0
                ? Math.round(
                    rows.reduce<number>(
                      (acc, r) => acc + (Number(r.score) || 0),
                      0,
                    ) / submittedCount,
                  ) + "%"
                : "-"}
            </div>
          </div>
          <div className="results-stat-card">
            <div
              className="results-stat-icon"
              style={{ background: "#fee2e2", color: "#dc2626" }}
            >
              <AlertCircle size={20} strokeWidth={2.5} />
            </div>
            <div className="results-stat-label">Not Attended</div>
            <div className="results-stat-value">{notAttendedCount}</div>
          </div>
        </div>
      </div>

      <div className="lp-table-panel" style={{ marginTop: "16px" }}>
        <div
          className="lp-table-panel-head"
          style={{
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e2e8f0",
            background: "#f8fafc",
          }}
        >
          <h2
            style={{
              fontSize: "1.15rem",
              fontWeight: 800,
              color: "#0f172a",
              margin: 0,
            }}
          >
            Candidate Performance
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ClipboardList size={18} color="#64748b" />
            <span
              style={{ fontSize: "0.95rem", fontWeight: 600, color: "#475569" }}
            >
              {total} candidate{total === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        <div className="lp-table-scroll-area" style={{ maxHeight: "640px" }}>
          {error ? (
            <div className="is-empty">
              <ClipboardList
                size={48}
                color="#cbd5e1"
                style={{ margin: "0 auto" }}
              />
              <h3>Could Not Load Results</h3>
              <p>{error}</p>
            </div>
          ) : rows.length === 0 && !loading ? (
            <div className="is-empty">
              <ClipboardList
                size={48}
                color="#cbd5e1"
                style={{ margin: "0 auto" }}
              />
              <h3>No Results Yet</h3>
              <p>No candidates have scores for this assessment yet.</p>
            </div>
          ) : (
            <table className="learner-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Status</th>
                  {/* <th>Jozuna</th>
                  <th>Program</th>
                  <th>Industry</th> */}
                  <th>Score</th>
                  <th>Submitted</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const grade = gradeConfig(row.score);
                  const candidateName =
                    [row.first_name, row.last_name].filter(Boolean).join(" ") ||
                    row.email_address;
                  return (
                    <tr
                      key={row.candidate_id}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleRowClick(row)}
                    >
                      <td>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <strong style={{ color: "#0f172a" }}>
                            {candidateName}
                          </strong>
                          <span
                            style={{
                              color: "#94a3b8",
                              fontSize: "0.85rem",
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Mail size={12} /> {row.email_address}
                          </span>
                        </div>
                      </td>
                      <td>{getStatusBadge(row)}</td>
                      {/* <td>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                          {row.jozuna_score !== null && row.jozuna_score !== undefined && row.jozuna_score !== 'NA' ? `${Math.round(Number(row.jozuna_score))}%` : '-'}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                          {row.programming_score !== null && row.programming_score !== undefined && row.programming_score !== 'NA' ? `${Math.round(Number(row.programming_score))}%` : '-'}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                          {row.industry_score !== null && row.industry_score !== undefined && row.industry_score !== 'NA' ? `${Math.round(Number(row.industry_score))}%` : '-'}
                        </span>
                      </td> */}
                      <td>
                        <span
                          style={{
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "13px",
                            fontWeight: 700,
                            backgroundColor: grade.bg,
                            color: grade.color,
                          }}
                        >
                          {row.score !== null &&
                          row.score !== undefined &&
                          row.score !== "NA"
                            ? `${Math.round(Number(row.score))}%`
                            : "-"}
                        </span>
                      </td>
                      <td style={{ color: "#64748b", fontSize: "0.9rem" }}>
                        {formatDateTime(row.submitted_at)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {row.skill_report && row.skill_report.length > 0 && (
                          <button
                            className="action-btn detail"
                            title="View skills"
                            onClick={() => handleRowClick(row)}
                          >
                            <Users size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedCandidateRow && (
        <div
          className="asmt-modal-overlay"
          onClick={() => setSelectedCandidateRow(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="asmt-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "900px",
              maxHeight: "85vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            <div
              className="asmt-modal-header"
              style={{
                padding: "20px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#f8fafc",
              }}
            >
              <div>
                <h2
                  style={{ margin: 0, fontSize: "1.25rem", color: "#0f172a" }}
                >
                  Skill Report:{" "}
                  {[
                    selectedCandidateRow.first_name,
                    selectedCandidateRow.last_name,
                  ]
                    .filter(Boolean)
                    .join(" ") || selectedCandidateRow.email_address}
                </h2>
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "0.9rem",
                    color: "#64748b",
                    display: "flex",
                    gap: "24px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#3b82f6",
                      }}
                    />
                    JozHRM Soft Skills:{" "}
                    <strong>
                      {selectedCandidateRow.jozuna_score !== null &&
                      selectedCandidateRow.jozuna_score !== undefined &&
                      selectedCandidateRow.jozuna_score !== "NA"
                        ? `${Math.round(Number(selectedCandidateRow.jozuna_score))}%`
                        : "N/A"}
                    </strong>
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#8b5cf6",
                      }}
                    />
                    Programming:{" "}
                    <strong>
                      {selectedCandidateRow.programming_score !== null &&
                      selectedCandidateRow.programming_score !== undefined &&
                      selectedCandidateRow.programming_score !== "NA"
                        ? `${Math.round(Number(selectedCandidateRow.programming_score))}%`
                        : "N/A"}
                    </strong>
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#ec4899",
                      }}
                    />
                    Industry:{" "}
                    <strong>
                      {selectedCandidateRow.industry_score !== null &&
                      selectedCandidateRow.industry_score !== undefined &&
                      selectedCandidateRow.industry_score !== "NA"
                        ? `${Math.round(Number(selectedCandidateRow.industry_score))}%`
                        : "N/A"}
                    </strong>
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginLeft: "auto",
                      background: "#f8fafc",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    Overall Score:{" "}
                    <strong style={{ color: "#0f172a" }}>
                      {selectedCandidateRow.score !== null &&
                      selectedCandidateRow.score !== undefined &&
                      selectedCandidateRow.score !== "NA"
                        ? `${Math.round(Number(selectedCandidateRow.score))}%`
                        : "N/A"}
                    </strong>
                  </span>
                </div>
              </div>
              <button
                className="asmt-modal-close"
                onClick={() => setSelectedCandidateRow(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "8px",
                }}
              >
                <X size={20} color="#64748b" />
              </button>
            </div>

            <div
              className="asmt-modal-body"
              style={{ padding: "24px", overflowY: "auto" }}
            >
              {selectedCandidateRow.skill_report &&
              selectedCandidateRow.skill_report.length > 0 ? (
                <div
                  className="asmt-dimension-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "20px",
                  }}
                >
                  {selectedCandidateRow.skill_report.map((dim, i) => (
                    <div
                      key={dim.dimension_id || i}
                      className="asmt-dimension-card"
                      style={{
                        padding: 0,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        background: "#ffffff",
                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <div
                        className="asmt-dimension-top"
                        style={{
                          backgroundColor: "#e6f3eb",
                          padding: "12px 16px",
                          margin: 0,
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <h3
                          style={{
                            margin: 0,
                            fontSize: "0.95rem",
                            color: "#1e293b",
                          }}
                        >
                          {dim.dimension_name || "Dimension"}
                        </h3>
                      </div>

                      <div style={{ padding: "16px" }}>
                        {(dim.skill_score || []).length > 0 ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            {dim.skill_score!.map((sk, j) => {
                              const pct = Math.round(sk.score || 0);
                              let chipBg = "#fee2e2";
                              let chipColor = "#dc2626";
                              if (pct >= 80) {
                                chipBg = "#dcfce7";
                                chipColor = "#15803d";
                              } else if (pct >= 60) {
                                chipBg = "#fef3c7";
                                chipColor = "#b45309";
                              }

                              return (
                                <div
                                  key={j}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      color: "#475569",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {sk.skill_name || "Unknown Skill"}
                                  </span>
                                  <span
                                    style={{
                                      backgroundColor: chipBg,
                                      color: chipColor,
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      fontSize: "0.8rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {pct}%
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p
                            className="asmt-result-nodata"
                            style={{
                              margin: 0,
                              fontSize: "0.85rem",
                              color: "#64748b",
                            }}
                          >
                            No skill data available.
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    color: "#64748b",
                  }}
                >
                  <Users
                    size={48}
                    color="#cbd5e1"
                    style={{ margin: "0 auto 16px auto" }}
                  />
                  <h3>No Skill Data</h3>
                  <p>
                    This candidate does not have detailed skill performance data
                    yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AssessmentResultsPage;
